import platform from '../../util/platform';
import { TOUCH_SUPPORT } from '../../util/supportedEvents';
import { State } from '../StateTypes';
import { UserAgent } from './userAgentTypes';

/**
 * Selects user agent state slice
 *
 * @param state
 */
export const selectUserAgent = (state: State): UserAgent => state.userAgent;

/**
 * Returns true if unit is mobile in portrait mode
 *
 * @param state
 */
export const selectIsMobilePortrait = (state: State): boolean => {
  const { isMobile, isPortrait } = selectUserAgent(state);
  return isMobile && isPortrait;
};

/**
 * Returns true if unit is mobile landscape
 *
 * @param state
 */
export const selectIsMobileLandscape = (state: State): boolean => {
  const { isMobile, isLandscape } = selectUserAgent(state);
  return isMobile && isLandscape;
};

/**
 * Returns true if static tool tip should be used
 *
 * @param state
 */
export const selectUseStaticToolTip = (state: State): boolean => {
  const { isMobile, isTablet, isDesktop } = selectUserAgent(state);
  return (
    platform.isKiosk || isMobile || isTablet || (isDesktop && TOUCH_SUPPORT)
  );
};

/**
 * Select Device type
 *
 * @param state
 */
export const selectDeviceType = (state: State): string =>
  selectUserAgent(state).deviceType;

/**
 * Select if mobile or tablet
 *
 * @param state
 */
export const selectIsMobileOrTablet = (state: State) => {
  const { isMobile, isTablet } = selectUserAgent(state);
  return isMobile || isTablet;
};

/**
 * Select is tablet portrait
 *
 * @param state
 * @returns {*}
 */
export const selectIsTabletPortrait = (state: State) => {
  const { isTablet, isPortrait } = selectUserAgent(state);
  return isTablet && isPortrait;
};

/**
 * Select is portrait
 *
 * @param state
 */
export const selectIsPortrait = (state: State) =>
  selectUserAgent(state).isPortrait;

/**
 * Select is landscape
 *
 * @param state
 */
export const selectIsLandscape = (state: State) =>
  selectUserAgent(state).isLandscape;

/**
 * Select is Mobile
 *
 * @param state
 */
export const selectIsMobile = (state: State) => selectUserAgent(state).isMobile;

export const selectIsKiosk = (state: State) =>
  selectUserAgent(state).deviceType === 'kiosk';

/**
 * Select is tablet
 *
 * @param state
 */
export const selectIsTablet = (state: State) => selectUserAgent(state).isTablet;

/**
 * Select orientation
 *
 * @param state
 */
export const selectOrientation = (state: State) =>
  selectUserAgent(state).orientation;

/**
 * Selects necessary data to determine that this was rendered on a larger display
 * in landscape mode
 */
export const selectIsWidescreen = (state: State) =>
  !selectIsMobile(state) &&
  (!selectIsTablet(state) || !selectIsPortrait(state));
